<template>
  <div>
    <breadcrumbs
      :breadcrumb-items="breadcrumbItems"
    />
    <UserToolbar
      v-if="account && Object.keys(account).length !== 0"
      :account="account"
    />
    <b-card>
      <CreativeToolbar
        :banner="banner"
        closing
      />
    </b-card>

    <CreativeNavigation v-if="authUserData.role.includes('admin')" />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-bottom">
            <div class="head-card-top mb-2">
              <b-button-group>
                <b-button
                  :to="{ name: 'creatives-rtb-stats-overview', params: { userId: $route.params.userId, id: $route.params.id, bannerId: $route.params.bannerId } }"
                  :variant="$route.name === 'campaign-rtb-stats-overview' ? 'primary' : 'outline-primary'"
                  pill
                  exact
                >
                  {{ $t('rtb.overview') }}
                </b-button>
                <b-button
                  :to="{ name: 'creatives-rtb-stats-sources', params: { userId: $route.params.userId, id: $route.params.id, bannerId: $route.params.bannerId } }"
                  :variant="$route.name === 'campaign-rtb-stats-sources' ? 'primary' : 'outline-primary'"
                  pill
                  exact
                >
                  {{ $t('rtb.sources') }}
                </b-button>
              </b-button-group>
            </div>
          </div>
          <div class="head-card-top">
            <h4>DSP Sources statistics</h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              v-model="rangePicker"
              :is-loading="loading"
              @range-change="toggleRange"
              @refresh="refreshData"
            />
          </div>
        </div>
      </div>
      <div
        v-if="loading"
        class="d-flex justify-content-center mb-1"
      >
        <b-spinner label="Loading..." />
      </div>
      <div v-else>
        <b-table
          small
          :items="statsV2Data"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          responsive
          striped
          foot-clone
        >
          <template #head(partner_id)>
            {{ $t('DSP Partner') }}
          </template>

          <template #head(impressions)>
            {{ $t('message.tableHeader.impressions') }}
          </template>

          <template #head(clicks)>
            {{ $t('message.tableHeader.clicks') }}
          </template>

          <template #head(ctr)>
            CTR
          </template>

          <template #head(revenue)>
            {{ $t('message.tableHeader.ecpm') }}
          </template>

          <template #head(spent)>
            {{ $t('stats.spent') }}
          </template>

          <template #cell(partner_id)="data">
            <router-link
              :to="{ name: 'creatives-rtb-stats-partner', params: { userId: $route.params.userId, id: $route.params.id, partnerId: data.value }}"
              class="text-primary"
            >
              <strong>{{ getPartnerName(data.value) || data.value || 'Unknown' }}</strong>
            </router-link>
          </template>

          <template #cell(impressions)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
          </template>

          <template #cell(clicks)="data">
            {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
          </template>

          <template #cell(ctr)="data">
            {{ data.item.clicks ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(data.item.clicks / data.item.impressions * 100) + '%' : 0 }}
          </template>

          <template #cell(revenue)="data">
            {{ currency('USD', data.value) }}
          </template>

          <template #cell(spent)="data">
            {{ currency(campaign.currency || 'USD', data.value) }}
          </template>

          <template #foot(partner_id)="data">
            <strong :data-label="data.label">{{ $t('Records') }}: {{ statsV2Data.length }}</strong>
          </template>

          <template #foot(impressions)="data">
            <strong :data-label="data.label">{{ totalFormat('impressions').toLocaleString('Ru-ru') }}</strong>
          </template>

          <template #foot(clicks)="data">
            <strong :data-label="data.label">{{ totalFormat('clicks').toLocaleString('Ru-ru') }}</strong>
          </template>

          <template #foot(ctr)="data">
            <strong :data-label="data.label">
              {{ totalFormat('clicks') ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(totalFormat('clicks') / totalFormat('impressions') * 100) : 0 }}%
            </strong>
          </template>

          <template #foot(revenue)="data">
            <strong :data-label="data.label">{{ currency('USD', totalFormat('revenue')) }}</strong>
          </template>

          <template #foot(spent)="data">
            <strong :data-label="data.label">{{ currency(campaign.currency || 'USD', totalFormat('spent')) }}</strong>
          </template>
        </b-table>
      </div>
    </b-card>
  </div>
</template>

<script>
import { getUserData } from '@/auth/utils'
import useJwt from '@/auth/jwt/useJwt'
import Ripple from 'vue-ripple-directive'
import {
  BCard,
  BTable,
  BSpinner,
  VBTooltip,
  BButton,
  BButtonGroup,
} from 'bootstrap-vue'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate from '@/utils/date-formatter'
import UserToolbar from '@/views/dashboard/users/UserToolbar.vue'
import CreativeNavigation from '@/views/dashboard/creatives/CreativeNavigation.vue'
import CreativeToolbar from '@/views/dashboard/creatives/CreativeToolbar.vue'
import StatsDateRangeFilter from '@/views/dashboard/dsppartners/stats/StatsDateRangeFilter.vue'

export default {
  name: 'RtbStatsSources',
  components: {
    Breadcrumbs,
    BCard,
    BTable,
    BSpinner,
    UserToolbar,
    CreativeNavigation,
    CreativeToolbar,
    StatsDateRangeFilter,
    BButton,
    BButtonGroup,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      loading: true,
      banner: {},
      pageTitle: '',
      pageSubtitle: '',
      breadcrumbItems: [],
      account: {},
      campaign: {},
      partnerNames: {},
      fields: [
        { key: 'partner_id', label: 'Partner', tdClass: 'td-width' },
        { key: 'impressions', label: 'Impressions', tdClass: 'td-width' },
        { key: 'clicks', label: 'Clicks', tdClass: 'td-width' },
        { key: 'ctr', label: 'CTR', tdClass: 'td-width' },
        { key: 'revenue', label: 'Revenue', tdClass: 'td-width' },
        { key: 'spent', label: 'Spent', tdClass: 'td-width' },
      ],
      sortBy: 'revenue',
      sortDesc: true,
      sortDirection: 'asc',
      authUserData: getUserData(),
      currency: currencyFormatter,
      getFormattedDate,
      statsV2Data: [],
      rangePicker: [
        getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        getFormattedDate(new Date()),
      ],
      rangeDefault: [
        getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        getFormattedDate(new Date()),
      ],
    }
  },

  created() {
    this.getUser()
    this.getCampaignData()
  },
  methods: {
    async getUser() {
      const responseData = await useJwt.getUser(this.$route.params.userId)
      this.account = responseData.data.account || {}
    },
    async getCampaignData() {
      const responseData = await useJwt.getCampaign(this.$route.params.id)
      this.campaign = responseData.data.campaign || {}
      await this.getBannerData()
      this.setBreabcrumbs()
      this.getStatsV2Data()
    },
    async getStatsV2Data() {
      this.loading = true
      this.statsV2Data = []

      if (!this.rangePicker || this.rangePicker.length < 2) {
        this.loading = false
        return
      }

      const params = {
        campaign_id: this.$route.params.id,
        ad_id: this.$route.params.bannerId,
        from: this.rangePicker[0],
        to: this.rangePicker[1],
        type: 'daily',
      }
      const responseData = await useJwt.getPhoenixDspPartnerTotal(params)
      this.statsV2Data = responseData.data.items || []

      // Load partner names
      await this.loadPartnerNames()

      this.loading = false
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('Users'),
          to: '/users/',
        },
        {
          text: this.account.name,
          to: `/users/${this.$route.params.userId}/`,
        },
        {
          text: this.$t('Campaigns'),
          to: `/users/${this.$route.params.userId}/campaigns`,
        },
        {
          text: this.campaign.title,
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}`,
        },
        {
          text: this.$t('Creatives'),
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}/creatives`,
        },
        {
          text: this.banner.title,
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}/creatives/${this.$route.params.bannerId}`,
        },
        {
          text: 'DSP Sources',
          active: true,
        },
      ]
    },

    totalFormat(field) {
      let total = 0
      this.statsV2Data.forEach(i => {
        if (field === 'partner_id') return // Skip partner_id field for totals
        total += i[field] || 0
      })
      return total
    },
    toggleRange(e) {
      this.rangePicker = e
      this.getStatsV2Data()
      this.$router.replace({
        name: this.$route.name,
        query: { from: e[0], to: e[1] },
      })
    },
    refreshData() {
      this.getStatsV2Data()
    },
    async loadPartnerNames() {
      try {
        const responseData = await useJwt.dsppartners()
        const partners = responseData.data.dsppartners || []
        this.partnerNames = {}
        partners.forEach(partner => {
          this.partnerNames[partner._id] = partner.name
        })
      } catch (error) {
        // Error loading partner names
      }
    },
    getPartnerName(partnerId) {
      return this.partnerNames[partnerId] || null
    },
    async getBannerData() {
      const responseData = await useJwt.getBanner(this.$route.params.bannerId)
      this.banner = responseData.data.banner || {}
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
<style>
  .col-picker {
    min-width: 220px;
  }
</style>
